import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from 'react-redux';
import TimeSelector from '../TimeSelector';
import { useAppDispatch, useAppSelector, useWidgetParam, useTranslate, useWidgetsTheme } from '@src/core/hooks';
import { useWidgetId } from '@src/core/IdProvider';
import type { RootState } from '@src/core/store';
import { SellingMode } from '@src/core/buyerApiClient';
import {
  loadAdderState,
  loadDefaultTariffGroup,
  adderResumeSelector,
  productSelector,
  productAdderStepState,
  addProductToCart,
  currentProductAdderStatus,
  loadTariffGroupAttributes,
  productAttributeTypesSelector,
  productHasLanguageStepSelector,
  tariffGroupAttributesLoadedSelector,
} from '@src/stores/product';
import { getOrderedEmbedSlots } from '@src/domains/products/features/product-booking/core/getOrderedEmbedSlots';
import {
  trackWidgetViewed,
  trackCtaClicked,
  resetStepTracking,
} from '@src/domains/products/features/product-adder-v2/analytics/widgetTracking';
import { loadProductUpsellOffers, productUpsellOffersStatusSelector } from '@src/stores/productUpsell';
import { completeCartSelector } from '@src/features/cart/store/cart';
import DateSelector from '@src/domains/products/features/product-booking/components/DateSelector';
import TariffSelector from '@src/domains/products/features/product-booking/components/TariffSelector';
import SeatmapSelector from '@src/domains/products/features/product-booking/components/SeatmapSelector';
import AdditionalsSelector from '@src/domains/products/features/product-booking/components/AdditionalsSelector';
import TrendingReservations from '@src/domains/products/features/product-booking/components/TrendingReservations';
import PriceContainer from '@src/domains/products/features/product-booking/components/PriceContainer';
import ProductBookingSummary from '@src/domains/products/features/product-booking/components/ProductBookingSummary';
import UniTheme from '@src/common/ui/UniTheme';
import { useUnitDisplayPrice } from '@src/domains/products/features/product-booking/hooks/useUnitDisplayPrice';
import { Card, CtaButton, Slot, SlotsList, Wrapper } from './styles';
import LanguageSelector from '../LanguageSelector';
import AttributeSelector from '../AttributeSelector';

import { SequentialStepsProvider } from '../../providers/SequentialStepsProvider';
import BookingStatus from '../BookingStatus';
import { IconShoppingCart } from '@planne-software/uni/icons';
import TrustBadges from '../TrustBadges';
import BookingError from '../BookingError';
import { useGuardedAddToCartClick } from '../../hooks/useGuardedAddToCartClick';
import useFeatureFlag from '@src/hooks/useFeatureFlag';

const ProductBookingContent = () => {
  const widgetId = useWidgetId();
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const { i18n } = useTranslate();
  const { isFeatureFlagEnabled } = useFeatureFlag();
  const customCtaText = useWidgetParam('add-to-cart-cta-text');
  const productId = useWidgetParam('product-id');
  const actionColor = useWidgetsTheme('actionColor');
  const product = useAppSelector(productSelector(productId ?? ''));
  const { currentStep } = useAppSelector(productAdderStepState(widgetId));

  const upsellOffersStatusSelector = useMemo(() => productUpsellOffersStatusSelector(productId ?? ''), [productId]);
  const { isLoading: isLoadingUpsellOffers } = useAppSelector(upsellOffersStatusSelector);

  const currentStatus = useAppSelector(currentProductAdderStatus(widgetId));

  const isAddingToCart = useMemo(() => currentStatus === 'adding_to_cart', [currentStatus]);

  // Upsell offers start loading on mount, so isLoadingUpsellOffers is true before any click. Only
  // factor that loading into the CTA once add-to-cart has been triggered at least once — from then
  // on the loading stays even if isAddingToCart goes back to false while the upsell is still loading.
  // Once nothing is loading anymore, reset the flag so a fresh add-to-cart starts from a clean state.
  const [hasTriggeredAddToCart, setHasTriggeredAddToCart] = useState(false);

  useEffect(() => {
    if (isAddingToCart) {
      setHasTriggeredAddToCart(true);
    } else if (!isLoadingUpsellOffers) {
      setHasTriggeredAddToCart(false);
    }
  }, [isAddingToCart, isLoadingUpsellOffers]);

  const { unitPrice, isLoading: isUnitPriceLoading } = useUnitDisplayPrice(widgetId, productId ?? undefined);
  const { totalCents } = useAppSelector(adderResumeSelector(widgetId));

  const hasLanguageStep = useAppSelector(productHasLanguageStepSelector(productId ?? ''));
  const tariffGroupAttributesLoaded = useAppSelector(tariffGroupAttributesLoadedSelector(productId ?? ''));
  const attributeTypes = useAppSelector(productAttributeTypesSelector(productId ?? ''));
  const widgetViewedProductIdRef = useRef<string | null>(null);

  const handleAddToCartCallback = useCallback(() => {
    const state = store.getState();
    const { productId: clickProductId, totalCents: clickTotalCents } = adderResumeSelector(widgetId)(state);
    const embedSlots = getOrderedEmbedSlots(widgetId, state);
    trackCtaClicked(clickProductId, clickTotalCents, embedSlots.length);
    dispatch(addProductToCart(widgetId));
  }, [dispatch, widgetId, store]);

  const handleAddToCart = useGuardedAddToCartClick(handleAddToCartCallback);

  const ctaLabel = customCtaText || i18n('product_booking_add_to_cart');

  const isProductLoading = !product;
  const dateSlot = product?.sellingMode === SellingMode.WithoutDate ? null : <DateSelector />;
  const timeSlot = product?.sellingMode === SellingMode.WithDateAndTime ? <TimeSelector /> : null;
  const isWithoutDate = product?.sellingMode === SellingMode.WithoutDate;
  const languageSlot = isWithoutDate ? null : <LanguageSelector />;
  const attributeSlots = isWithoutDate
    ? null
    : attributeTypes.map((type) => <AttributeSelector key={type} type={type} />);

  const cardRef = useRef<HTMLDivElement>(null);

  const { cart } = useAppSelector(completeCartSelector);
  const cartId = cart?.id;
  const isProductUpsellEnabled = isFeatureFlagEnabled('PR-4007-upsell');

  useEffect(() => {
    resetStepTracking();
  }, []);

  useEffect(() => {
    if (productId) {
      dispatch(loadAdderState(widgetId, productId));
      dispatch(loadDefaultTariffGroup(productId));
      dispatch(loadTariffGroupAttributes(productId));
    }
  }, [dispatch, widgetId, productId]);

  // Wait for attributes to load — has_language_step is date-independent and unavailable on first render.
  useEffect(() => {
    if (product && productId && tariffGroupAttributesLoaded && widgetViewedProductIdRef.current !== productId) {
      widgetViewedProductIdRef.current = productId;
      trackWidgetViewed(productId, product.appId, hasLanguageStep);
    }
  }, [product, productId, tariffGroupAttributesLoaded, hasLanguageStep]);

  useEffect(() => {
    if (isProductUpsellEnabled && productId && cartId) {
      dispatch(loadProductUpsellOffers(productId));
    }
  }, [dispatch, isProductUpsellEnabled, productId, cartId]);

  return (
    <Wrapper>
      <UniTheme>
        <Card ref={cardRef}>
          <TrendingReservations />

          <PriceContainer
            unitPrice={unitPrice}
            installment={null}
            totalCents={totalCents > 0 ? totalCents : null}
            loading={isUnitPriceLoading}
          />

          <SlotsList>
            {isProductLoading ? (
              Array.from({ length: 5 }).map((_, index) => <Slot key={index} data-testid='adder-slot-skeleton' />)
            ) : (
              <>
                {dateSlot}
                {languageSlot}
                {attributeSlots}
                <TariffSelector />
                {timeSlot}
                <SeatmapSelector />
                <AdditionalsSelector />
              </>
            )}
          </SlotsList>

          <BookingError />

          <CtaButton
            disabled={isAddingToCart || (hasTriggeredAddToCart && isProductUpsellEnabled && isLoadingUpsellOffers)}
            loading={isAddingToCart || (hasTriggeredAddToCart && isProductUpsellEnabled && isLoadingUpsellOffers)}
            type='button'
            actionColor={actionColor}
            variant='contained'
            fullWidth
            startSlot={<IconShoppingCart />}
            onClick={handleAddToCart}
          >
            {ctaLabel}
          </CtaButton>

          <TrustBadges />

          {currentStep === 'finish' ? <BookingStatus /> : null}
        </Card>
        <ProductBookingSummary cardRef={cardRef} unitPrice={unitPrice} />
      </UniTheme>
    </Wrapper>
  );
};

const ProductBooking = () => (
  <SequentialStepsProvider>
    <ProductBookingContent />
  </SequentialStepsProvider>
);

export default ProductBooking;
